/**
 * OfflineUnlockCodeCard — reveal the current offline-TOPUP code for a loan so
 * support can read it to a customer whose payment cleared the due installment
 * but whose phone is offline (paid by USSD / cash, no data).
 *
 * The code is normally folded into the payment-confirmation SMS automatically
 * (the device's DPC auto-reads it and unlocks itself). This card is the manual
 * fallback — for a customer who didn't get or lost that SMS. The customer enters
 * the 8-digit code on their lock screen ("Fungua kwa msimbo") to unlock for a
 * bounded grace window (≈ one repayment period); the device re-syncs and the
 * server reconciles the true lock state on its next connection. The DPC verifies
 * the code ENTIRELY on-device against its sealed snapshot key — no server
 * round-trip from the phone.
 *
 * Behaviour (mirrors ServicePinCard's audit-once posture)
 *   - The card is invisible to users without `unlock_code.issue` — the parent
 *     gates rendering. It is also DPC-only (android-do), since the code only
 *     verifies against a DPC snapshot key.
 *   - Nothing is fetched on load. Only "Reveal code" calls the endpoint, and
 *     every reveal is audited server-side (action='loan.offline_unlock_code
 *     .reveal'; the code itself is never in the audit payload). One row per
 *     deliberate operator action, not per page-view.
 *   - PAYMENT-GATED: the server only issues a code when a settled payment has
 *     CLEARED THE CURRENT INSTALLMENT (the loan is caught up). A loan still in
 *     arrears returns NoValidCurrentPayment — surfaced as a clear refusal, since
 *     a topup is only meaningful once a qualifying payment exists.
 *   - The reveal also returns the device's online/last-seen + lock status and
 *     the last settled payment — the evidence the agent checks before reading
 *     the code aloud. If the device is actually online, a hint says the code is
 *     usually unnecessary (it should sync on its own).
 *   - The code auto-hides after 60s (or manual hide / unmount). It rotates each
 *     time bucket, so the revealed value is only valid for ~1–2 buckets.
 *   - 404 means the device has no active snapshot key (pre-enrollment,
 *     post-offboarding, or not DPC-controlled) — surfaced as an explanatory
 *     line, since the code couldn't be redeemed on that device anyway.
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Badge } from "./Badge.js";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

interface OfflineUnlockCodeResponse {
  readonly code: string;
  readonly bucketExpiresAt: string;
  readonly lastPayment: { readonly amountMinor: number; readonly settledAt: string } | null;
  readonly device: {
    readonly lastSeenAt: string | null;
    readonly online: boolean;
    readonly lockState: string;
  };
}

const AUTO_HIDE_MS = 60_000;

export function OfflineUnlockCodeCard({
  loanId,
  currency,
}: {
  readonly loanId: string;
  readonly currency: string;
}): JSX.Element {
  const [data, setData] = useState<OfflineUnlockCodeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reveal = useMutation({
    mutationFn: () =>
      api.get<OfflineUnlockCodeResponse>(`/api/loans/${loanId}/offline-unlock-code`),
    onSuccess: (res) => {
      setData(res);
      setError(null);
    },
    onError: (err: unknown) => {
      setData(null);
      const e = err as { status?: number; problem?: { detail?: string; code?: string } };
      if (e.status === 404) {
        setError(
          "No active snapshot key for this device — an offline code cannot be derived. " +
            "The device is pre-enrollment, post-offboarding, or not DPC-controlled.",
        );
      } else if (e.problem?.code === "NoValidCurrentPayment") {
        setError(
          "No qualifying payment yet — a topup code unlocks only after a payment clears the " +
            "current installment. This loan is still in arrears, so there is nothing to top up.",
        );
      } else {
        setError(e.problem?.detail ?? "Failed to reveal the offline topup code.");
      }
    },
  });

  // Auto-hide after 60s so a screenshare / shoulder-surf window is bounded.
  // Operators who need it longer can re-reveal (and re-audit).
  useEffect(() => {
    if (data === null) return;
    const timeout = window.setTimeout(() => setData(null), AUTO_HIDE_MS);
    return () => window.clearTimeout(timeout);
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Offline topup</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <p className="text-slate-600">
          Manual fallback for the offline-topup code — for a customer whose payment cleared the due
          installment but whose phone is offline (and who didn&apos;t get the code in their payment
          SMS). They enter the 8-digit code on the lock screen (&quot;Fungua kwa msimbo&quot;) to
          unlock for a bounded grace window; the device re-syncs and the server reconciles on its
          next connection. The server only issues a code once a settled payment has cleared the
          current installment; it rotates each time bucket and every reveal is audited.
        </p>

        {data === null ? (
          <div className="flex items-center gap-3">
            <Button onClick={() => reveal.mutate()} disabled={reveal.isPending} variant="primary">
              {reveal.isPending ? "Revealing…" : "Reveal code"}
            </Button>
            {error !== null ? <span className="text-xs text-red-700">{error}</span> : null}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Device status — confirm the customer really is offline + locked. */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={data.device.online ? "success" : "warning"}>
                {data.device.online ? "Device online" : "Device offline"}
              </Badge>
              <Badge tone={data.device.lockState === "UNLOCKED" ? "success" : "danger"}>
                {data.device.lockState}
              </Badge>
              <span className="text-xs text-slate-500">
                {data.device.lastSeenAt
                  ? `Last seen ${new Date(data.device.lastSeenAt).toLocaleString()}`
                  : "Never seen"}
              </span>
            </div>

            {data.device.online ? (
              <p className="text-xs text-amber-700">
                This device is currently online — it should unlock on its own once it syncs. An
                offline code is usually unnecessary here.
              </p>
            ) : null}

            {/* Last settled payment — the support agent's evidence. */}
            <p className="text-xs text-slate-500">
              {data.lastPayment
                ? `Last settled payment: ${formatMoney(data.lastPayment.amountMinor, currency)} on ${new Date(
                    data.lastPayment.settledAt,
                  ).toLocaleString()}.`
                : "No settled payment on record for this loan."}
            </p>

            {/* The code. */}
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl font-bold tracking-widest text-slate-900">
                {data.code}
              </span>
              <Badge tone="warning">Auto-hides in 60s</Badge>
              <Button onClick={() => setData(null)} variant="ghost">
                Hide
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Valid until about {new Date(data.bucketExpiresAt).toLocaleTimeString()} (then it
              rotates). Read it to the customer; do not send it over an unencrypted channel.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
